

import { Button } from '@/shared/ui/button';
import { DialogFooter } from '@/shared/ui/dialog';
import {
    Form,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/shared/ui/form';
import { Input } from "@/shared/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/shared/ui/select';
import { Switch } from '@/shared/ui/switch';
import { mastersApi } from '@/features/masters/api';
import { UserRole } from '@/features/users/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import * as z from 'zod';

// バリデーションスキーマ
const formSchema = z.object({
    username: z.string().trim().min(1, "ユーザー名は必須です。").max(50, "ユーザー名は50文字以内です。"),
    name: z.string().trim().min(1, "氏名は必須です。").max(50, "氏名は50文字以内です。"),
    email: z.string().trim().email("有効なメールアドレスを入力してください。"),
    password: z.string().min(6, "パスワードは6文字以上で入力してください。"),
    role: z.nativeEnum(UserRole, { message: "権限を選択してください。" }),
    is_active: z.boolean(),
    craftsman_id: z.string().nullable(),
});

type UserFormData = z.infer<typeof formSchema>;


/**
 * ユーザー追加フォームのプロパティ
 */
interface AddUserFormProps {
    onClose: () => void;
    onSave: (data: UserFormData) => Promise<void>;
}

/**
 * 職人データ
 */
interface Craftsman {
    id: string;
    name: string;
}

/**
 * ユーザー追加フォーム
 * @param props プロパティ
 * @returns ユーザー追加フォーム
 */
export default function AddUserForm({ onClose, onSave }: AddUserFormProps) {
    const [isLoading, setIsLoading] = useState(false);
    const [craftsmen, setCraftsmen] = useState<Craftsman[]>([]);
    const [craftsmenLoading, setCraftsmenLoading] = useState(false);

    const form = useForm<UserFormData>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            username: '',
            name: '',
            email: '',
            password: '',
            role: UserRole.USER,
            is_active: true,
            craftsman_id: null
        },
    });

    // 職人データを取得
    useEffect(() => {
        const fetchCraftsmen = async () => {
            setCraftsmenLoading(true);
            try {
                const data = await mastersApi.craftsmen.getAll();
                // dataがundefinedまたはnullの場合は空配列を使用
                const safeCraftsmenData = Array.isArray(data) ? data : [];
                setCraftsmen(safeCraftsmenData);
            } catch (error) {
                console.error('職人データの取得に失敗しました:', error);
                toast.error('職人データの読み込みに失敗しました');
                // エラー時は空配列を設定
                setCraftsmen([]);
            } finally {
                setCraftsmenLoading(false);
            }
        };

        fetchCraftsmen();
    }, []);

    const onSubmit = async (values: UserFormData) => {
        setIsLoading(true);
        try {
            await onSave(values);
            toast.success("新規ユーザーが作成されました。");
            onClose();
        } catch (error) {
            console.error("Failed to create user in AddUserForm:", error);
            const errorMessage = error instanceof Error ? error.message : "ユーザーの作成に失敗しました。";
            toast.error(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6" autoComplete="off">
                <FormField
                    control={form.control}
                    name="username"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>ユーザー名</FormLabel>
                            <FormControl>
                                <Input
                                    placeholder="ユーザー名を入力"
                                    autoComplete="off"
                                    {...field}
                                />
                            </FormControl>
                            <FormDescription>
                                ログイン時に使用するユーザー名です。
                            </FormDescription>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>氏名</FormLabel>
                            <FormControl>
                                <Input
                                    placeholder="氏名を入力"
                                    autoComplete="off"
                                    {...field}
                                />
                            </FormControl>
                            <FormDescription>
                                表示用の氏名です。
                            </FormDescription>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>メールアドレス</FormLabel>
                            <FormControl>
                                <Input
                                    type="email"
                                    placeholder="メールアドレスを入力"
                                    autoComplete="off"
                                    {...field}
                                />
                            </FormControl>
                            <FormDescription>
                                ログインに使用するメールアドレスです。
                            </FormDescription>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>パスワード</FormLabel>
                            <FormControl>
                                <Input
                                    type="password"
                                    placeholder="パスワードを入力"
                                    autoComplete="new-password"
                                    {...field}
                                />
                            </FormControl>
                            <FormDescription>
                                6文字以上のパスワードを設定してください。
                            </FormDescription>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name="role"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>権限</FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                                <FormControl>
                                    <SelectTrigger>
                                        <SelectValue placeholder="権限を選択" />
                                    </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                    {Object.values(UserRole).map((role) => (
                                        <SelectItem key={role} value={role}>
                                            {role}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name="craftsman_id"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>職人との紐づけ</FormLabel>
                            <Select
                                onValueChange={(value) => {
                                    field.onChange(value === "none" ? null : value);
                                }}
                                defaultValue={field.value || "none"}
                                disabled={craftsmenLoading}
                            >
                                <FormControl>
                                    <SelectTrigger>
                                        <SelectValue placeholder="職人を選択（紐づけない場合は空白）" />
                                    </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                    <SelectItem value="none">紐づけなし</SelectItem>
                                    {craftsmen.map((craftsman) => (
                                        <SelectItem key={craftsman.id} value={craftsman.id}>
                                            {craftsman.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <FormDescription>
                                このユーザーに紐づける職人を選択します。紐づけるとカレンダーで担当予定が強調表示されます。
                            </FormDescription>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name="is_active"
                    render={({ field }) => (
                        <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4 shadow-sm">
                            <div className="space-y-0.5">
                                <FormLabel className="text-base">有効状態</FormLabel>
                                <FormDescription>
                                    ユーザーアカウントを有効または無効にします。
                                </FormDescription>
                            </div>
                            <FormControl>
                                <Switch
                                    checked={field.value}
                                    onCheckedChange={field.onChange}
                                />
                            </FormControl>
                        </FormItem>
                    )}
                />

                <DialogFooter className="pt-4">
                    <Button type="button" variant="outline" onClick={onClose} className="mr-2">
                        キャンセル
                    </Button>
                    <Button type="submit" disabled={isLoading}>
                        {isLoading ? (
                            <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> 作成中...</>
                        ) : (
                            'ユーザーを作成'
                        )}
                    </Button>
                </DialogFooter>
            </form>
        </Form>
    );
} 